import React from 'react';

import { faArrowUpRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useUpdateReservationType } from 'src/features/reservations/hooks/useUpdateReservationType';
import { RESERVATION_STATUS, type ReservationType } from 'src/features/reservations/types/reservationType';
import { reservationMessages } from 'src/features/reservations/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
    GeneralBlockValue,
} from 'src/registry/components/blocks/GeneralBlock';
import { toast } from 'src/registry/hooks/UseToast';
import { Switch } from 'src/registry/ui/Switch';
import { generalErrorMessages } from 'src/utils/messages';

type ReservationMetricsProps = {
    selectedReservationType?: ReservationType;
};

const ReservationMetrics = ({ selectedReservationType }: ReservationMetricsProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();
    const navigate = useNavigate();

    const [isActive, setIsActive] = React.useState<boolean | undefined>(selectedReservationType?.enabled);
    const [selectedVersionId, setSelectedVersionId] = React.useState<string>('');

    const createGeneralBlock = ({
        title,
        blockValue,
        navigationPath,
    }: {
        title: string;
        blockValue: string | number;
        navigationPath: string;
    }) => {
        return (
            <GeneralBlock className="cursor-pointer lg:w-1/4" onClick={() => void navigate(navigationPath)}>
                <GeneralBlockHeader className="flex justify-between">
                    <GeneralBlockTitle>{title}</GeneralBlockTitle>
                    <FontAwesomeIcon icon={faArrowUpRight} className="text-primary-700" />
                </GeneralBlockHeader>
                <GeneralBlockBody>
                    <GeneralBlockValue>{blockValue}</GeneralBlockValue>
                </GeneralBlockBody>
            </GeneralBlock>
        );
    };

    React.useEffect(() => {
        if (selectedReservationType) {
            setIsActive(selectedReservationType?.enabled);
            setSelectedVersionId(selectedReservationType?.id);
        }
    }, [selectedReservationType]);

    const totalReservations = selectedReservationType?.total_reservations_count
        ? selectedReservationType?.total_reservations_count
        : '0';
    const totalActiveReservations = selectedReservationType?.active_reservations_count
        ? selectedReservationType?.active_reservations_count
        : '0';
    const upcomingReservations = selectedReservationType?.upcoming_reservations_count
        ? selectedReservationType?.upcoming_reservations_count
        : '0';

    const { mutate: updateActiveStatus } = useUpdateReservationType({
        onSuccess: () => {
            toast({
                title: formatMessage(reservationMessages.editActiveStatusSuccess),
                description: formatMessage(reservationMessages.editActiveStatusSuccessDescription),
            });
        },
        onError: (error) => {
            const errorData = error.response?.data;
            const errorCode = errorData?.code ?? formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorData?.message ?? formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    const navigationURL = `/vehicles/reservations?reservation_type=${selectedReservationType?.reservation_type}&vehicle_stay_reservation_type=${selectedReservationType?.id}`;

    if (!parkingId) return null;

    return (
        <div className="grid h-full grid-cols-2 gap-4 lg:flex lg:justify-between">
            {createGeneralBlock({
                title: formatMessage(reservationMessages.activeReservations),
                blockValue: totalActiveReservations,
                navigationPath: `${navigationURL}&status=${RESERVATION_STATUS.ACTIVE}`,
            })}
            {createGeneralBlock({
                title: formatMessage(reservationMessages.upcomingReservations),
                blockValue: upcomingReservations,
                navigationPath: `${navigationURL}&status=${RESERVATION_STATUS.UPCOMING}`,
            })}
            {createGeneralBlock({
                title: formatMessage(reservationMessages.totalReservations),
                blockValue: totalReservations,
                navigationPath: `${navigationURL}`,
            })}

            <GeneralBlock className="lg:w-1/4">
                <GeneralBlockHeader className="flex justify-between">
                    <GeneralBlockTitle>{formatMessage(reservationMessages.status)}</GeneralBlockTitle>
                    <Switch
                        checked={isActive}
                        disabled={!selectedReservationType}
                        onCheckedChange={(val) => {
                            const submitData = {
                                parkingId: parkingId,
                                versionId: selectedVersionId,
                                enabled: val,
                            };
                            setIsActive(val);
                            updateActiveStatus({ ...submitData });
                        }}
                    />
                </GeneralBlockHeader>
                <GeneralBlockBody>
                    <GeneralBlockValue>
                        {isActive
                            ? formatMessage(reservationMessages.active)
                            : formatMessage(reservationMessages.inactive)}
                    </GeneralBlockValue>
                </GeneralBlockBody>
            </GeneralBlock>
        </div>
    );
};

export default ReservationMetrics;
